import {
	mainActiveSidebar$,
	mainSidebarPosition$,
	useAppDrawerWidth,
} from "$root/components/AppDrawer/AppDrawer-shared";
import AuthorizationGuard from "$root/components/AuthorizationGuard";
import type { SidebarPositions } from "$root/components/common/Sidebar-shared";
import { useAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { type CSSProperties, useRef, useState } from "react";
import { DOCKED_CHAT_WIDTH, OVERLAY_CHAT_WIDTH, useChatDockState, useChatPanelWidth } from "./chatDock";
import { SphereAIPanelResizeHandle } from "./SphereAIPanelResizeHandle";
import { SphereAISidebarContent } from "./SphereAISidebarContent";

/**
 * The Sphere AI chat, rendered as a direct flex child of the app shell row (see PlatformRouter)
 * so it can reserve layout space when docked. A single stable element toggles between:
 * - **docked** — `position: relative`, in-flow, animating its width open/closed; the page reflows.
 * - **overlay** — `position: fixed`, out of flow, sliding in over the page (narrow viewports).
 *
 * The element never changes parent, so switching modes (including crossing the dock viewport
 * threshold on resize) is a pure style change — the live `useChat` instance inside is preserved.
 *
 * Left/right docking is placed by flex `order`: a left-docked panel takes `order: -1` and the nav
 * rail is given the same order (earlier in source), so it slots between the rail and the content.
 *
 * The `featureFlags.sphereAI` kill switch is checked by the caller (PlatformRouter), so this
 * component — and everything it mounts — simply does not exist when the flag is off.
 *
 * In both docked and overlay modes the width is the user's to set, by dragging the grip on the
 * panel's inner edge (see SphereAIPanelResizeHandle); `null` means "still at the mode default". The
 * drag is capped at half the viewport, past which the header's fullscreen toggle is the right tool.
 */
/** @internal — exported for tests */
export function panelStyle({
	isDocked,
	fullscreen,
	open,
	position,
	appDrawerWidth,
	width,
	resizing,
}: {
	isDocked: boolean;
	fullscreen: boolean;
	open: boolean;
	position: SidebarPositions;
	appDrawerWidth: number;
	width: number | null;
	resizing: boolean;
}): CSSProperties {
	if (isDocked) {
		return {
			// Left-docked slots between the nav rail (also order -1, earlier in source) and the page.
			order: position === "left" ? -1 : 0,
			// Sticky + viewport-tall, exactly like the AppDrawer nav rail (max-h-dvh sticky top-0):
			// stays pinned as the page scrolls and bounds the height so the chat body scrolls
			// internally instead of the whole panel scrolling away with the page.
			position: "sticky",
			top: 0,
			alignSelf: "flex-start",
			height: "100dvh",
			width: open ? (width ?? DOCKED_CHAT_WIDTH) : 0,
			// No animation mid-drag: the width is already following the pointer, and easing it would
			// lag a frame behind the handle.
			transition: resizing ? undefined : "width 300ms ease-in-out",
			[position === "left" ? "borderRight" : "borderLeft"]: `1px solid ${themeCSSVars.palette_N100}`,
		};
	}
	// Fullscreen: a fixed overlay filling everything to the right of the nav drawer (which stays
	// on top, so we reserve its width instead of sliding under it), sliding in from the docked side.
	if (fullscreen) {
		return {
			position: "fixed",
			top: 0,
			bottom: 0,
			left: appDrawerWidth,
			right: 0,
			transform: open ? "translateX(0)" : position === "right" ? "translateX(100%)" : "translateX(-100%)",
			transition: "transform 300ms ease-in-out",
			boxShadow: open ? "0 10px 30px rgba(0,0,0,0.15)" : undefined,
		};
	}
	// Overlay: fixed, so it's pulled out of flex flow (reserves no space) and floats over the page.
	return {
		position: "fixed",
		top: 0,
		bottom: 0,
		width: width ?? OVERLAY_CHAT_WIDTH,
		left: position === "left" ? appDrawerWidth : undefined,
		right: position === "right" ? 0 : undefined,
		transform: open ? "translateX(0)" : position === "right" ? "translateX(100%)" : "translateX(-100%)",
		transition: "transform 300ms ease-in-out",
		boxShadow: open ? "0 10px 30px rgba(0,0,0,0.15)" : undefined,
	};
}

export function SphereAIDockPanel(): React.JSX.Element {
	const [showSidebar, setShowSidebar] = useAtom(mainActiveSidebar$);
	const [position, setPosition] = useAtom(mainSidebarPosition$);
	const { isDocked, fullscreen } = useChatDockState();
	const appDrawerWidth = useAppDrawerWidth();
	// The drag measures the panel (the only place the default docked width — a CSS clamp — exists as a
	// number) and moves the guide line; the grip is state so the drag listeners can attach on mount.
	const panelRef = useRef<HTMLDivElement | null>(null);
	const guideRef = useRef<HTMLDivElement | null>(null);
	const [handleEl, setHandleEl] = useState<HTMLElement | null>(null);
	const { width, resizing, nudgeWidth, resetWidth } = useChatPanelWidth({
		panelRef,
		handleEl,
		guideRef,
		position,
	});

	const open = showSidebar === "chat";
	// Fullscreen owns the whole width, so there's no edge to drag; closed, there's no panel to size.
	const resizable = open && !fullscreen;
	const contentWidth = fullscreen ? "100%" : (width ?? (isDocked ? DOCKED_CHAT_WIDTH : OVERLAY_CHAT_WIDTH));

	// Mount on first open and stay mounted (like the old Sidebar's unmountOnExit={false}) so
	// background tasks and in-flight streaming survive the panel being closed.
	const [mounted, setMounted] = useState(false);
	if (open && !mounted) {
		setMounted(true);
	}

	return (
		<AuthorizationGuard requiredService="ASK_SPHERE">
			{() =>
				mounted ? (
					<div
						ref={panelRef}
						data-qualifier="SphereAIDockPanel"
						data-docked={isDocked ? "true" : undefined}
						className="z-[999] flex shrink-0 grow-0 flex-col overflow-hidden bg-white"
						style={panelStyle({ isDocked, fullscreen, open, position, appDrawerWidth, width, resizing })}
					>
						{/* Fixed-width inner box so a docked width collapse clips rather than reflowing the content. */}
						<div className="flex h-full flex-col" style={{ width: contentWidth }}>
							<SphereAISidebarContent
								open={open}
								onClose={() => setShowSidebar(null)}
								position={position}
								setPosition={setPosition}
								width={contentWidth}
							/>
						</div>
						{resizable && (
							<SphereAIPanelResizeHandle
								position={position}
								resizing={resizing}
								innerRef={setHandleEl}
								guideRef={guideRef}
								onNudge={nudgeWidth}
								onReset={resetWidth}
							/>
						)}
					</div>
				) : (
					<></>
				)
			}
		</AuthorizationGuard>
	);
}
